/**
 * Did the boot loader draw before this bundle arrived?
 *
 * `frontend/index.html` carries the building section as static markup so a page load or a
 * reload has something on screen from the first paint (generated by `tooling/gen-boot.mjs`).
 * React clears it by itself — `createRoot(container).render()` replaces `#root`'s children on
 * the first commit — which is the whole removal story. What it cannot do by itself is tell the
 * app that the loader *was* there, and two things downstream need to know:
 *
 *   1. `useLongWait` must not spend its 200 ms drawing nothing. On a cold start the loader is
 *      already up, so the React one has to take over on the very first render or the screen
 *      goes blank in the middle of a wait that never stopped.
 *   2. `PageLoader` must join the 7s loop where the boot copy had reached, not restart it.
 *
 * So the answer is read once, in `app/main.tsx`, before `createRoot` — after that the DOM no
 * longer holds it and no component may go looking. A module variable rather than context: it
 * is one immutable fact about how this page started, and threading a provider through the tree
 * for it would be ceremony around a boolean.
 */

let showedLoader = false;

/** Called once from `app/main.tsx`, before the first render. */
export function markBoot(shown: boolean): void {
  showedLoader = shown;
}

/**
 * True when this page started with the boot loader on screen — a load or a reload, never a
 * client-side navigation. Read it in a state initialiser, not during every render: the fact is
 * about the page's first moment, and by the second wait it is the wrong thing to act on.
 */
export function bootShowedLoader(): boolean {
  return showedLoader;
}
